import { useMemo } from 'react';
import { Brain, ClipboardList } from 'lucide-react';
import { appConfig } from '../../../constants';
import { formatDateParts, formatScore, formatShortDate, getActivityScores } from '../utils/formatDate';
import { getSubjectColor } from '../utils/subjectColors';
import { toAssignmentList } from '../utils/toAssignmentList';
import { shouldRevealSolutions } from '../../../utils';

export const TYPE_TABS = [
  { id: 'all', label: 'All' },
  { id: 'self-practice', label: 'Self Practice' },
  { id: 'assignments', label: 'Assignments' }
];

const TYPE_TAB_IDS = new Set(TYPE_TABS.map((tab) => tab.id));

const asText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    return value.name || value.title || value.assignmentName || value.label || '';
  }
  return '';
};

const startCaseStatus = (status) => {
  const value = String(status || 'pending').toLowerCase().replace(/[_-]+/g, ' ');
  return value.replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeSubjectKey = (subject) => {
  const value = asText(subject).trim();
  if (!value || value.toLowerCase() === 'default') {
    return 'others';
  }
  return value.toLowerCase();
};

const getDisplayName = (key, rawSubject) => {
  if (key === 'others') return 'Others';
  return rawSubject || 'Others';
};

const getPdfUrl = (assignment) => {
  if (assignment.evaluatedPdfUrl) return assignment.evaluatedPdfUrl;
  const pdfKey = assignment.marksheetData?.pdfKey;
  if (!pdfKey) return null;
  const base = String(appConfig.apiBaseUrl || '').replace(/\/?$/, '/');
  return `${base}file/download?key=${encodeURIComponent(pdfKey)}&action=view`;
};

const getStatus = (assignment) => {
  const raw = String(assignment.status || '').toUpperCase().replace(/[-\s]+/g, '_');
  if (raw === 'COMPLETED') return 'completed';
  if (raw === 'STARTED' || raw === 'PAUSED' || raw === 'IN_PROGRESS') return 'in-progress';
  if (raw === 'YET_TO_START') return 'yet-to-start';
  return 'pending';
};

const getFilterType = (assignment) => {
  const raw = asText(
    assignment.filterType ||
      assignment.category ||
      assignment.assignmentType ||
      assignment.type ||
      assignment.mode ||
      assignment.assignment?.assignmentType ||
      assignment.assignment?.type ||
      assignment.chatType
  ).toLowerCase();

  if (
    assignment.isPractice === true ||
    assignment.isSelfPractice === true ||
    raw.includes('practice') ||
    raw.includes('practise') ||
    raw.includes('adaptive') ||
    raw.includes('self')
  ) {
    return 'self-practice';
  }

  return 'assignments';
};

const matchesSearch = (activity, searchQuery) => {
  if (!searchQuery.trim()) return true;
  const query = searchQuery.toLowerCase();
  return (
    activity.title.toLowerCase().includes(query) ||
    activity.subtitle.toLowerCase().includes(query)
  );
};

const matchesTab = (activity, selectedTab) => {
  if (!selectedTab || selectedTab === 'all') return true;
  if (selectedTab === 'self-practice' || selectedTab === 'practice') {
    return activity.filterType === 'self-practice';
  }
  if (selectedTab === 'assignments') {
    return activity.category === 'assignments' || activity.filterType === 'assignments';
  }
  return activity.subjectKey === selectedTab;
};

const sortByDateDesc = (a, b) => {
  if (!a.sortTime && !b.sortTime) return 0;
  if (!a.sortTime) return 1;
  if (!b.sortTime) return -1;
  return b.sortTime - a.sortTime;
};

const pickTimestamp = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue;
    const parsed = value instanceof Date ? value : new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
};

const getScheduleDates = (assignment) => {
  const nested =
    assignment?.assignment && typeof assignment.assignment === 'object' ? assignment.assignment : {};

  return {
    startDate: pickTimestamp(assignment.startDate, nested.startDate),
    dueDate: pickTimestamp(assignment.dueDate, nested.dueDate)
  };
};

const mapAssignment = (assignment) => {
  const { obtained: obtainedScore, total: totalScore } = getActivityScores(assignment);
  const status = getStatus(assignment);
  const scoreLabel =
    status === 'completed' || (Number.isFinite(obtainedScore) && obtainedScore > 0)
      ? formatScore(obtainedScore, totalScore)
      : null;
  const statusText = startCaseStatus(assignment.status);
  const subject = asText(assignment.subject) || asText(assignment.assignment?.subject);
  const subjectKey = normalizeSubjectKey(subject);
  const colors = getSubjectColor(subjectKey === 'others' ? 'others' : subject);
  const { startDate: startSource, dueDate: endSource } = getScheduleDates(assignment);
  const sortSource = pickTimestamp(startSource, endSource, assignment.createdAt, assignment.startedAt);
  const sortTime = sortSource ? sortSource.getTime() : 0;
  const teacherName = asText(assignment.teacher?.name || assignment.teacher || assignment.teacherName);
  const title =
    asText(assignment.assignmentName) ||
    asText(assignment.assignment?.assignmentName) ||
    asText(assignment.assignment?.name) ||
    asText(assignment.name) ||
    'Untitled Assignment';
  const filterType = getFilterType(assignment);
  const TypeIcon = filterType === 'self-practice' ? Brain : ClipboardList;
  const isOffline =
    assignment.source === 'offline' ||
    Boolean(assignment.evaluatedPdfUrl || assignment.marksheetData?.pdfKey);
  const assignmentId =
    assignment.assignmentId ||
    assignment.assignment?._id ||
    assignment.assignment?.id ||
    (typeof assignment.assignment === 'string' ? assignment.assignment : null);
  const isDueDatePassed = Boolean(endSource && endSource.getTime() < Date.now());

  return {
    id: assignment._id || assignment.id || String(Math.random()),
    studentAssignmentId: assignment._id || assignment.id,
    assignmentId: assignmentId ? String(assignmentId) : null,
    title,
    subtitle: [subject, teacherName].filter(Boolean).join(' • ') || 'Assignment',
    subject,
    subjectKey,
    subjectName: getDisplayName(subjectKey, subject),
    details: statusText,
    typeName:
      filterType === 'self-practice'
        ? 'Self Practice'
        : isOffline
          ? 'Offline Assignment'
          : 'Online Assignment',
    filterType,
    revealSolutions: shouldRevealSolutions(assignment),
    // RN always tags these rows as assignments so the All tab shows the full list.
    category: 'assignments',
    createdAt: startSource || null,
    updatedAt: endSource || null,
    sortTime,
    startParts: formatDateParts(startSource),
    endParts: formatDateParts(endSource),
    displayDate: formatShortDate(startSource),
    date: formatShortDate(startSource),
    score: scoreLabel,
    status,
    canView: status === 'completed',
    canResume: status === 'in-progress' || status === 'yet-to-start' || status === 'pending',
    isDueDatePassed,
    isOffline,
    pdfUrl: getPdfUrl(assignment),
    pdfKey: assignment.marksheetData?.pdfKey || null,
    TypeIcon,
    colors
  };
};

export const useActivityGroups = ({
  assignments,
  selectedTab,
  mobileFilter,
  searchQuery = ''
}) => {
  const allActivities = useMemo(() => {
    return toAssignmentList(assignments)
      .filter((assignment) => assignment && typeof assignment === 'object')
      .map(mapAssignment);
  }, [assignments]);

  const desktopRows = useMemo(() => {
    return allActivities
      .filter((activity) => matchesSearch(activity, searchQuery) && matchesTab(activity, selectedTab))
      .sort(sortByDateDesc);
  }, [allActivities, selectedTab, searchQuery]);

  const subjectGroups = useMemo(() => {
    const filtered = allActivities
      .filter((activity) => matchesSearch(activity, searchQuery) && matchesTab(activity, mobileFilter))
      .sort(sortByDateDesc);

    const groupMap = new Map();

    for (const activity of filtered) {
      if (!groupMap.has(activity.subjectKey)) {
        groupMap.set(activity.subjectKey, {
          id: activity.subjectKey,
          name: activity.subjectName,
          colors: activity.colors,
          activities: []
        });
      }
      groupMap.get(activity.subjectKey).activities.push(activity);
    }

    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.id === 'others') return 1;
      if (b.id === 'others') return -1;
      return a.id.localeCompare(b.id);
    });
  }, [allActivities, mobileFilter, searchQuery]);

  const desktopTabs = useMemo(() => {
    const groupMap = new Map();

    for (const activity of allActivities) {
      if (!groupMap.has(activity.subjectKey)) {
        groupMap.set(activity.subjectKey, {
          id: activity.subjectKey,
          label: activity.subjectName
        });
      }
    }

    const subjectTabs = Array.from(groupMap.values()).sort((a, b) => {
      if (a.id === 'others') return 1;
      if (b.id === 'others') return -1;
      return a.id.localeCompare(b.id);
    });

    return [...TYPE_TABS, ...subjectTabs];
  }, [allActivities]);

  return {
    allActivities,
    desktopRows,
    subjectGroups,
    desktopTabs,
    mobileTabs: TYPE_TABS,
    isEmpty: subjectGroups.length === 0,
    isTypeTab: TYPE_TAB_IDS.has(selectedTab)
  };
};
